// Keep processed uploads comfortably below the API's 5 MB per-file limit.
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;
export const MAX_UPLOAD_BYTES = 1024 * 1024;
// Site1 renders the hero at 3:2 on desktop and crops its centre to 4:3 on mobile.
export const HEADER_ASPECT = 3 / 2;
export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export const validateImageFile = (file) => {
  if (!IMAGE_TYPES.includes(file.type)) throw new Error("format");
  if (file.size > MAX_SOURCE_BYTES) throw new Error("sourceSize");
};

export const loadBrandingImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => {
    if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 80_000_000) {
      reject(new Error("dimensions"));
    } else resolve(image);
  };
  image.onerror = () => reject(new Error("decode"));
  image.src = url;
});

// Position is a fraction of the available travel, shared by CSS preview and
// canvas export. Nothing can pan outside the image and reveal a blank edge.
export const cropRectangle = (width, height, aspect, zoom = 1, x = 0.5, y = 0.5) => {
  const baseWidth = Math.min(width, height * aspect);
  const cropWidth = baseWidth / Math.max(1, zoom);
  const cropHeight = cropWidth / aspect;
  return {
    x: (width - cropWidth) * Math.min(1, Math.max(0, x)),
    y: (height - cropHeight) * Math.min(1, Math.max(0, y)),
    width: cropWidth,
    height: cropHeight,
  };
};

const toBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("encode")), type, quality);
});

export const exportBrandingImage = async (image, { kind, crop, zoom, x, y }) => {
  const rect = crop
    ? cropRectangle(image.naturalWidth, image.naturalHeight, kind === "logo" ? 1 : HEADER_ASPECT, zoom, x, y)
    : { x: 0, y: 0, width: image.naturalWidth, height: image.naturalHeight };
  const maxDimension = kind === "logo" ? 512 : 1920;
  let scale = Math.min(1, maxDimension / Math.max(rect.width, rect.height));
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("encode");
  // Some browsers fall back to PNG when WebP encoding is unavailable. Reduce
  // dimensions as well as quality so even that fallback stays within budget.
  for (let attempt = 0; attempt < 6; attempt += 1) {
    canvas.width = Math.max(1, Math.round(rect.width * scale));
    canvas.height = Math.max(1, Math.round(rect.height * scale));
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(image, rect.x, rect.y, rect.width, rect.height, 0, 0, canvas.width, canvas.height);
    const blob = await toBlob(canvas, "image/webp", Math.max(0.65, 0.9 - attempt * 0.05));
    if (blob.size <= MAX_UPLOAD_BYTES) {
      const extension = blob.type === "image/webp" ? "webp" : "png";
      return new File([blob], `${kind}.${extension}`, { type: blob.type });
    }
    scale *= 0.8;
  }
  throw new Error("encode");
};

export const brandingSaveError = (error, t, fallback) => {
  if (error.response?.status === 413) return t("Dashboard.BrandingImages.errors.requestSize");
  if (error.response?.status === 400 && error.response.data?.code === "IMAGE_VALIDATION_FAILED") return t("Dashboard.BrandingImages.errors.upload");
  if (!error.response) return t("Dashboard.BrandingImages.errors.network");
  return t(fallback);
};
